import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import ItemPicker from '../components/ItemPicker'
import MoneyInput from '../components/MoneyInput'
import { nodePath } from '../lib/contracts'
import { emptyExpenseForm, expenseFormProblem, expenseInput, formFromExpense, todayIso, type ExpenseForm } from '../lib/invoices'
import type { CostTree } from '../lib/tree'
import type { CostExpense } from '../types'

const Field: React.FC<{ id?: string; label: string; required?: boolean; className?: string; children: React.ReactNode }> = ({ id, label, required, className, children }) => (
  <div className={cn('space-y-1.5', className)}>
    <Label htmlFor={id}>{label}{required ? ' *' : ''}</Label>
    {children}
  </div>
)

interface Props {
  open: boolean
  mode: 'create' | 'edit'
  expense?: CostExpense | null
  tree: CostTree
  currency: string
  onOpenChange: (open: boolean) => void
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

const ExpenseDialog: React.FC<Props> = ({ open, mode, expense, tree, currency, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<ExpenseForm>(() => emptyExpenseForm(todayIso()))
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(expense ? formFromExpense(expense) : emptyExpenseForm(todayIso()))
    setTouched(false)
    setSubmitting(false)
    // Only on opening: a background reload must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = <K extends keyof ExpenseForm>(key: K, value: ExpenseForm[K]) => setForm((f) => ({ ...f, [key]: value }))
  const problem = expenseFormProblem(form)
  const item = form.nodeId ? tree.byId.get(form.nodeId)?.activity : undefined
  const path = form.nodeId ? nodePath(tree, form.nodeId) : ''
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(expenseInput(form))
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(mode === 'create' ? 'CostControl_expense_new' : 'CostControl_expense_edit')}</DialogTitle>
          <DialogDescription>{t('CostControl_expense_dialog_hint')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <Field label={t('CostControl_expense_field_item')} required className="sm:col-span-2">
            <div className="flex flex-wrap items-center gap-3">
              <ItemPicker tree={tree} excludeIds={form.nodeId ? [form.nodeId] : []} label={t('CostControl_expense_pick_item')} onPick={(nodeId) => set('nodeId', nodeId)} />
              {item && (
                <div className="min-w-0 text-sm">
                  {path && <div className="truncate text-xs text-muted-foreground">{path}</div>}
                  <div className="truncate font-medium">{item.code ? `${item.code} · ${item.name}` : item.name}</div>
                </div>
              )}
            </div>
          </Field>
          <Field id="cc-exp-description" label={t('CostControl_expense_field_description')} required className="sm:col-span-2">
            <Input id="cc-exp-description" value={form.description} onChange={(e) => set('description', e.target.value)} />
          </Field>
          <Field id="cc-exp-supplier" label={t('CostControl_expense_field_supplier')}>
            <Input id="cc-exp-supplier" value={form.supplierName} onChange={(e) => set('supplierName', e.target.value)} />
          </Field>
          <Field id="cc-exp-date" label={t('CostControl_expense_field_date')} required>
            <IsoDatePicker id="cc-exp-date" value={form.date} onChange={(v) => set('date', v)} />
          </Field>
          <Field id="cc-exp-amount" label={`${t('CostControl_expense_field_amount')} (${currency})`} required>
            <MoneyInput id="cc-exp-amount" value={form.amount} onChange={(value, valid) => setForm((f) => ({ ...f, amount: value, amountValid: valid }))} />
          </Field>
          {touched && problem && <p className="text-sm text-destructive sm:col-span-2">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t(mode === 'create' ? 'Common_create' : 'Common_save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default ExpenseDialog
